import React, { useState } from 'react';
import { TrendingUp, ArrowRight, Heart, ChevronDown, ChevronUp } from 'lucide-react';
import { useChatInput } from '../../contexts/ChatContext';
import { usePopularSearches } from '../../hooks/usePopularSearches';
import { useAuth } from '../../components/auth/AuthContext';
import { SignInPrompt } from '../../components/auth/SignInPrompt';
import { useNavigate } from 'react-router-dom';

export const PopularSearches: React.FC = () => {
  const [showSignInPrompt, setShowSignInPrompt] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const { queries, isLoading, toggleLike } = usePopularSearches();
  const { user } = useAuth();
  const { setInputValue } = useChatInput();
  const navigate = useNavigate();

  const handleLikeClick = async (e: React.MouseEvent, searchId: string) => {
    e.preventDefault(); // Prevent Link navigation
    e.stopPropagation(); // Stop event bubbling
    if (!user) {
      setShowSignInPrompt(true);
      return;
    }
    await toggleLike(searchId);
  };

  const toggleExpand = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const isTextTruncated = (text: string): boolean => {
    const words = text.split(' ');
    const estimatedChars = text.length;
    return estimatedChars > 50 || words.length > 12;
  };

  const handleSearchClick = (query: string, searchId: string) => {
    setInputValue(query);
    navigate('/', { 
      state: { 
        query,
        searchMetadata: {
          searchId,
          searchCategory: 'popular_search'
        }
      } 
    });
  };

  return (
    <>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 transition-colors">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5 text-blue-500" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Popular Searches</h2>
        </div>
        
        {isLoading ? (
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                <div className="h-3 bg-gray-100 dark:bg-gray-600 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : queries.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400 text-center py-4">No searches yet</p>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {queries.map((item) => (
              <div
                key={item.id}
                className="w-full py-3 text-left transition-colors group hover:bg-gradient-to-r hover:from-gray-50 hover:to-transparent dark:hover:from-gray-700/50 dark:hover:to-transparent cursor-pointer"
              >
                <div className="px-3 flex justify-between items-start">
                  <div className="flex-1" onClick={() => handleSearchClick(item.query, item.id)}>
                    <div>
                      <p className={`text-gray-700 dark:text-gray-200 group-hover:text-gray-900 dark:group-hover:text-white font-medium ${
                        expandedItems.has(item.id) ? '' : 'line-clamp-2'
                      }`}>
                        {item.query}
                      </p>
                      {isTextTruncated(item.query) && (
                        <div 
                          onClick={(e) => toggleExpand(e, item.id)}
                          className="mt-1 text-blue-600 dark:text-blue-400 text-sm flex items-center gap-1 cursor-pointer hover:text-blue-700 dark:hover:text-blue-300"
                        >
                          {expandedItems.has(item.id) ? (
                            <>
                              Show less
                              <ChevronUp className="w-4 h-4" />
                            </>
                          ) : (
                            <>
                              Show more
                              <ChevronDown className="w-4 h-4" />
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 dark:text-gray-400">
                      <span>{item.searches.toLocaleString()} searches</span>
                      <span className="text-gray-300 dark:text-gray-600">•</span>
                      <span className="flex items-center gap-1">
                        <Heart className="w-3 h-3" />
                        {item.likes}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={(e) => handleLikeClick(e, item.id)}
                      className={`p-1.5 rounded-full transition-colors ${
                        item.isLiked
                          ? 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20'
                          : 'text-gray-400 hover:bg-gray-50 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300'
                      }`}
                      aria-label={item.isLiked ? 'Unlike search' : 'Like search'}
                    >
                      <Heart 
                        className="w-4 h-4" 
                        fill={item.isLiked ? 'currentColor' : 'none'} 
                      />
                    </button>
                    <div className="w-px h-4 bg-gray-200 dark:bg-gray-700"></div>
                    <button
                      onClick={() => handleSearchClick(item.query, item.id)}
                      className="p-1.5 rounded-full transition-colors hover:bg-gray-50 dark:hover:bg-gray-700"
                      aria-label="Run search"
                    >
                      <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-blue-500 dark:text-gray-500 dark:group-hover:text-blue-400 transition-colors" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showSignInPrompt && (
        <SignInPrompt onClose={() => setShowSignInPrompt(false)} />
      )}
    </>
  );
};